import { liveboardAndDataObjects } from "./extractHeadersLiveboards";

export interface Header {
  tableId: string;
  tableName: string;
  columnId: string;
  columnName: string;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

function isHeaderArray(arr: unknown): arr is Header[] {
  if (!Array.isArray(arr) || arr.length === 0) return false;
  return arr.every(
    (item) =>
      isPlainObject(item) &&
      typeof (item as any).tableId === "string" &&
      typeof (item as any).tableName === "string",
  );
}

/**
 * Depth‑first walk that collects every matching Header array.
 * `found` is mutated in‑place.
 */
function walk(
  node: unknown,
  found: Header[],
  visited = new Set<unknown>(),
): void {
  if (visited.has(node)) return;
  visited.add(node);

  if (Array.isArray(node)) {
    for (const el of node) walk(el, found, visited);
    return;
  }

  if (isPlainObject(node)) {
    // ── Look for the target property ───────────────────────
    if (Object.prototype.hasOwnProperty.call(node, "sources")) {
      const candidate = (node as any).sources;
      if (isHeaderArray(candidate)) {
        // push copies so later mutations don’t affect our result
        found.push(
          ...candidate.map((h) => ({
            tableId: h.tableId,
            tableName: h.tableName,
            columnId: h.columnId,
            columnName: h.columnName,
          })),
        );
      }
    }

    // ── Recurse into every own property ─────────────────────
    for (const key of Object.keys(node)) {
      walk((node as any)[key], found, visited);
    }
  }
}

/**
 * Extracts all unique `Header` objects from the data
 */
export async function extractUniqueHeadersData(staticData: any) {
  const resultObject: liveboardAndDataObjects[] = [];

  staticData.forEach((data: any) => {
    // Collect every header occurrence (may include duplicates)
    const allHeaders: Header[] = [];
    walk(data, allHeaders);

    // De‑duplicate using a Map keyed by `${id}|${name}`
    const uniqMap = new Map<string, Header>();
    const counts: Record<string, number> = {};

    for (const hdr of allHeaders) {
      const key = `${hdr.tableId}|${hdr.tableName}`;
      counts[key] = (counts[key] ?? 0) + 1;
      if (!uniqMap.has(key)) uniqMap.set(key, hdr);
    }

    const uniqueDataObjects = Array.from(uniqMap.values()).map((dataObject) => {
      return { id: dataObject.tableId, name: dataObject.tableName };
    });

    let epochSeconds: number = 0;

    if (data && data.metadata_header && data.metadata_header.created) {
      epochSeconds = data.metadata_header.created;
    }
    const dateObject: Date = new Date(epochSeconds);

    resultObject.push({
      parentID: data.metadata_id,
      parentName: data.metadata_name,
      creationDate: dateObject.toISOString() + " UTC",
      childObjects: uniqueDataObjects,
    });
  });

  return resultObject;
}
